import type { ToolArtifact } from '@web/adapter/toThreadMessages'
import { useOptionalBridge } from '@web/adapter/useBridgeRuntime'
import { PermissionCard } from '@web/components/PermissionCard'
import { Icon, type IconName } from '@web/components/ui/icons'
import { formatDuration } from '@web/state/lifecycle'
import { type ReactNode, useEffect, useState } from 'react'

export type ToolState =
  | 'pending'
  | 'approval'
  | 'running'
  | 'cancelled'
  | 'done'
  | 'error'
  | 'denied'

export function toolState(
  a: ToolArtifact | undefined,
  result: unknown,
  isError: boolean | undefined,
  _turnOpen: boolean,
): ToolState {
  if (a?.permission && !a.permission.resolved) return 'approval'
  if (a?.part.denial || a?.permission?.resolved?.behavior === 'deny')
    return 'denied'
  if (a?.permission?.resolved?.behavior === 'cancelled' && result === undefined)
    return 'cancelled'
  if (a?.status) return a.status
  if (result === undefined && !a?.part.summarizedBy) return 'pending'
  if (isError) return 'error'
  return 'done'
}

export function ToolShell({
  icon,
  name,
  summary,
  artifact,
  result,
  isError,
  children,
  headerExtra,
  hidePermission,
  pendingLabel = 'needs approval',
}: {
  icon: IconName
  name: string
  summary?: ReactNode
  artifact?: ToolArtifact
  result: unknown
  isError?: boolean
  children?: ReactNode
  headerExtra?: ReactNode
  /** A specialized presentation can provide its own permission placement. */
  hidePermission?: boolean
  pendingLabel?: string
}) {
  const bridge = useOptionalBridge()
  const turnOpen = bridge?.snapshot.acp.running ?? true
  const state = toolState(artifact, result, isError, turnOpen)
  const [isOpen, setOpen] = useState(false)
  const invocationId = artifact?.part.args?.invocationId
  const timedProgress =
    typeof invocationId === 'string'
      ? bridge?.snapshot.bridge.toolProgress[invocationId]
      : undefined
  const notes = artifact?.part.notes ?? []
  const elapsed = useToolElapsed(
    state,
    artifact?.startedTimestamp,
    artifact?.finishedTimestamp,
  )

  return (
    <div
      className={`tool-card my-1.5 min-w-0 rounded-control border ${state === 'approval' ? 'border-warn/60' : 'border-line'} bg-elevated shadow-sm`}
    >
      <button
        className="tool-card-toggle flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-hover/60"
        aria-expanded={isOpen}
        aria-label={`${name} details`}
        onClick={() => setOpen(!isOpen)}
      >
        {isOpen ? (
          <Icon name="chevronDown" size={13} className="shrink-0 text-faint" />
        ) : (
          <Icon name="chevronRight" size={13} className="shrink-0 text-faint" />
        )}
        <Icon name={icon} size={14} className="shrink-0 text-ok" />
        <span className="tool-card-name text-[12.5px] font-semibold text-fg">
          {name}
        </span>
        {summary && (
          <span className="mono min-w-0 flex-1 truncate text-[12px] text-muted">
            {summary}
          </span>
        )}
        {!summary && <span className="flex-1" />}
        {headerExtra}
        <StateBadge
          state={state}
          elapsed={elapsed}
          pendingLabel={pendingLabel}
        />
      </button>
      {isOpen && timedProgress && (
        <div className="tool-timed-progress">
          <div className="tool-timed-progress-detail">
            <span>{timedProgress.detail}</span>
            <strong>{Math.round(timedProgress.percentage)}%</strong>
          </div>
          <progress
            aria-label={`${name} progress`}
            max={100}
            value={timedProgress.percentage}
          />
        </div>
      )}
      {isOpen && (
        <div className="border-t border-line px-3 py-2">
          {children}
          {notes.map((n, i) => (
            <div
              key={i}
              className={`mt-1 text-[11px] ${n.level === 'warning' ? 'text-warn' : 'text-faint'}`}
            >
              {n.content}
            </div>
          ))}
          {artifact?.part.denial && !artifact.permission && (
            <div className="mt-2 text-xs text-danger">
              Denied: {artifact.part.denial.message}
            </div>
          )}
          {artifact?.permission && !hidePermission && (
            <PermissionCard request={artifact.permission} />
          )}
        </div>
      )}
      {!isOpen &&
        artifact?.permission &&
        !artifact.permission.resolved &&
        !hidePermission && (
          <div className="border-t border-line px-3 py-2">
            <PermissionCard request={artifact.permission} />
          </div>
        )}
    </div>
  )
}

function StateBadge({
  state,
  elapsed,
  pendingLabel,
}: {
  state: ToolState
  elapsed?: number
  pendingLabel: string
}) {
  switch (state) {
    case 'pending':
      return (
        <span className="chip shrink-0 text-muted">
          <Icon name="clock" size={11} /> Pending
        </span>
      )
    case 'approval':
      return (
        <span className="chip icon-anim shrink-0 text-warn">
          <Icon name="shieldAlert" size={11} /> {pendingLabel}
        </span>
      )
    case 'running':
      return (
        <span className="chip icon-anim shrink-0">
          <Icon name="loader" size={11} /> Running
          {elapsed !== undefined ? ` · ${formatDuration(elapsed)}` : ''}
        </span>
      )
    case 'cancelled':
      return (
        <span
          className="chip shrink-0 text-muted"
          title="The tool stopped before completion"
        >
          <Icon name="noResult" size={11} /> Interrupted
          {elapsed !== undefined ? ` · ${formatDuration(elapsed)}` : ''}
        </span>
      )
    case 'done':
      return (
        <span className="chip shrink-0 text-ok">
          <Icon name="check" size={11} /> Completed
          {elapsed !== undefined ? ` · ${formatDuration(elapsed)}` : ''}
        </span>
      )
    case 'error':
      return (
        <span className="chip shrink-0 text-danger">
          <Icon name="error" size={11} /> Failed
          {elapsed !== undefined ? ` · ${formatDuration(elapsed)}` : ''}
        </span>
      )
    case 'denied':
      return (
        <span className="chip shrink-0 text-danger">
          <Icon name="shieldX" size={11} /> Denied
        </span>
      )
  }
}

/** Update wall-clock tool runtime only while its active card can be seen. */
function useToolElapsed(
  state: ToolState,
  startedTimestamp: number | undefined,
  finishedTimestamp: number | undefined,
): number | undefined {
  const [now, setNow] = useState(() => Date.now() / 1000)
  useEffect(() => {
    if (state !== 'running' || startedTimestamp === undefined) return
    let interval: ReturnType<typeof setInterval> | undefined
    const update = () => setNow(Date.now() / 1000)
    const followVisibility = () => {
      if (interval !== undefined) clearInterval(interval)
      interval = undefined
      if (document.visibilityState === 'visible') {
        update()
        interval = setInterval(update, 100)
      }
    }
    followVisibility()
    document.addEventListener('visibilitychange', followVisibility)
    return () => {
      if (interval !== undefined) clearInterval(interval)
      document.removeEventListener('visibilitychange', followVisibility)
    }
  }, [state, startedTimestamp])
  if (startedTimestamp === undefined) return undefined
  const end = state === 'running' ? now : finishedTimestamp
  return end === undefined ? undefined : Math.max(0, end - startedTimestamp)
}

/** Shared block for tool output. */
export function OutputBlock({
  text,
  maxLines = 40,
  className,
}: {
  text: string
  maxLines?: number
  className?: string
}) {
  const [expanded, setExpanded] = useState(false)
  const lines = text.split('\n')
  const truncated = !expanded && lines.length > maxLines
  const shown = truncated ? lines.slice(0, maxLines).join('\n') : text
  if (!text) return null
  return (
    <div className={className}>
      <pre className="mono max-h-[60vh] overflow-auto whitespace-pre-wrap break-words rounded-control border border-line bg-inset px-3 py-2 text-[12px] leading-5">
        {shown}
      </pre>
      {lines.length > maxLines && (
        <button
          className="mt-1 text-[11px] text-muted hover:underline"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : `Show all ${lines.length} lines`}
        </button>
      )}
    </div>
  )
}

export function ElapsedHint({ seconds }: { seconds?: number }) {
  if (!seconds) return null
  return (
    <span className="chip icon-anim">
      <Icon name="clock" size={11} /> {Math.round(seconds)}s
    </span>
  )
}

export const resultText = (result: unknown): string => {
  if (!result) return ''
  if (typeof result === 'string') return result
  const r = result as { text?: string }
  return r.text ?? JSON.stringify(result, null, 2)
}
